/**
 * java.util.Random — bit-exact port of the 48-bit LCG.
 *
 * Exactness matters: the Daily Challenge seeds the spawner so every player gets the
 * same enemy sequence for a given day. A "close enough" PRNG would silently break that
 * contract. The 48-bit state is kept as two 24-bit halves so the hot path stays on
 * Numbers rather than BigInt.
 */

const MASK24 = 0xffffff;          // 2^24 - 1
const TWO24 = 0x1000000;          // 2^24
// 0x5DEECE66D split into 24-bit halves
const MULT_HI = 1502;
const MULT_LO = 15525485;
const ADDEND = 0xb;

export class Random {
  private hi = 0; // top 24 bits of the 48-bit seed
  private lo = 0; // bottom 24 bits

  private haveNextNextGaussian = false;
  private nextNextGaussian = 0;

  constructor(seed?: number | bigint) {
    if (seed === undefined) {
      // Java uses a nanotime-derived unique seed; any well-spread value works here.
      this.setSeed(BigInt(Date.now()) ^ (BigInt(Math.floor(Math.random() * 0xffffffff)) << 16n));
    } else {
      this.setSeed(seed);
    }
  }

  /** java.util.Random.setSeed: seed = (seed ^ 0x5DEECE66D) & ((1 << 48) - 1) */
  setSeed(seed: number | bigint): void {
    const s = (typeof seed === 'bigint' ? seed : BigInt(Math.trunc(seed)));
    const scrambled = (s ^ 0x5deece66dn) & 0xffffffffffffn;
    this.hi = Number((scrambled >> 24n) & 0xffffffn);
    this.lo = Number(scrambled & 0xffffffn);
    this.haveNextNextGaussian = false;
  }

  /** Advance the LCG: seed = (seed * 0x5DEECE66D + 0xB) mod 2^48 */
  private advance(): void {
    const sHi = this.hi;
    const sLo = this.lo;

    // (MULT_HI*2^24 + MULT_LO) * (sHi*2^24 + sLo) mod 2^48
    //   => mid*2^24 + low, dropping the 2^48 term
    const mid = (MULT_HI * sLo + MULT_LO * sHi) & MASK24;
    const low = MULT_LO * sLo + ADDEND;

    const newLo = low % TWO24;
    const carry = Math.floor(low / TWO24);

    this.lo = newLo;
    this.hi = (mid + carry) & MASK24;
  }

  /** java.util.Random.next(bits) */
  protected next(bits: number): number {
    this.advance();
    const shift = 48 - bits;
    let v: number;
    if (shift >= 24) {
      v = this.hi >>> (shift - 24);
    } else {
      v = this.hi * Math.pow(2, 24 - shift) + (this.lo >>> shift);
    }
    // Java's next() returns a signed int; only bits==32 can actually go negative.
    return bits === 32 ? v | 0 : v;
  }

  /** nextInt() — full signed 32-bit range. nextInt(bound) — [0, bound). */
  nextInt(bound?: number): number {
    if (bound === undefined) return this.next(32);

    const n = Math.trunc(bound);
    if (n <= 0) throw new Error('bound must be positive');

    // Power of two — Java takes the high bits.
    if ((n & -n) === n) {
      return Math.floor((n * this.next(31)) / 0x80000000);
    }

    let bits: number;
    let val: number;
    do {
      bits = this.next(31);
      val = bits % n;
    } while (bits - val + (n - 1) > 2147483647); // Java's overflow rejection, expressed positively
    return val;
  }

  nextLong(): bigint {
    const hi = BigInt(this.next(32));
    const lo = BigInt(this.next(32));
    return BigInt.asIntN(64, (hi << 32n) + lo);
  }

  nextBoolean(): boolean {
    return this.next(1) !== 0;
  }

  nextFloat(): number {
    return this.next(24) / (1 << 24);
  }

  nextDouble(): number {
    return (this.next(26) * 134217728 + this.next(27)) / 9007199254740992; // (2^26<<27 + 2^27) / 2^53
  }

  nextGaussian(): number {
    if (this.haveNextNextGaussian) {
      this.haveNextNextGaussian = false;
      return this.nextNextGaussian;
    }
    let v1: number, v2: number, s: number;
    do {
      v1 = 2 * this.nextDouble() - 1;
      v2 = 2 * this.nextDouble() - 1;
      s = v1 * v1 + v2 * v2;
    } while (s >= 1 || s === 0);
    const multiplier = Math.sqrt((-2 * Math.log(s)) / s);
    this.nextNextGaussian = v2 * multiplier;
    this.haveNextNextGaussian = true;
    return v1 * multiplier;
  }
}

/** java.util.Collections.shuffle(list, rnd) — Fisher-Yates, exactly as the JDK does it. */
export function shuffle<T>(list: T[], rnd: Random): void {
  for (let i = list.length; i > 1; i--) {
    const j = rnd.nextInt(i);
    const tmp = list[i - 1];
    list[i - 1] = list[j];
    list[j] = tmp;
  }
}
